import React from "react";
import useSWR from "swr";

interface ConfigContext {
  stripeKey: string;
  mapboxApiAccessToken: string;
  sentryDSN: string;
  environment: "development" | "staging" | "production";
  auth0Domain: string;
  auth0ClientId: string;
  auth0ApiAudience: string;
  auth0PortalDomain: string;
  auth0PortalClientId: string;
  auth0PortalApiAudience: string;
  splitioApiKey: string;
  tranzilaNotifyURLAddress: string;
}

const initialConfig: ConfigContext = {
  stripeKey: "",
  mapboxApiAccessToken: "",
  sentryDSN: "",
  environment: "development",
  auth0Domain: "",
  auth0ClientId: "",
  auth0ApiAudience: "",
  auth0PortalDomain: "",
  auth0PortalClientId: "",
  auth0PortalApiAudience: "",
  splitioApiKey: "",
  tranzilaNotifyURLAddress: "",
};

const ConfigContext = React.createContext<ConfigContext>(initialConfig);

export function useConfig() {
  return React.useContext(ConfigContext);
}

interface ConfigProviderProps {
  children?: React.ReactNode;
}

// We fetch config to be able to get staging vs production environment
export function ConfigProvider(props: ConfigProviderProps) {
  const { children } = props;
  const data = useConfigQuery();

  return (
    <ConfigContext.Provider
      value={{
        stripeKey: data?.stripeKey ?? "",
        mapboxApiAccessToken: data?.mapboxApiAccessToken ?? "",
        sentryDSN: data?.sentryDSN ?? "",
        environment: data?.environment ?? "",
        auth0Domain: data?.auth0Domain ?? "",
        auth0ClientId: data?.auth0ClientId ?? "",
        auth0ApiAudience: data?.auth0ApiAudience ?? "",
        auth0PortalDomain: data?.auth0PortalDomain ?? "",
        auth0PortalClientId: data?.auth0PortalClientId ?? "",
        auth0PortalApiAudience: data?.auth0PortalApiAudience ?? "",
        splitioApiKey: data?.splitioApiKey ?? "",
        tranzilaNotifyURLAddress: data?.tranzilaNotifyURLAddress ?? "",
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfigQuery() {
  const response = useSWR(
    "/web_ui_config",
    async (input) => {
      const res = await fetch(input);

      const body = await res.json();

      if (res.status >= 200 && res.status < 300) {
        return body;
      }

      throw new Error(body.message);
    },
    { suspense: true }
  );

  return {
    stripeKey: response.data.REACT_APP_STRIPE_PUBLISHABLE_KEY,
    mapboxApiAccessToken: response.data.REACT_APP_MAPBOX_API_ACCESS_TOKEN,
    sentryDSN: response.data.REACT_APP_SENTRY_DSN,
    environment: response.data.REACT_APP_ENVIRONMENT,
    auth0Domain: response.data.REACT_APP_AUTH0_DOMAIN,
    auth0ClientId: response.data.REACT_APP_AUTH0_CLIENT_ID,
    auth0ApiAudience: response.data.REACT_APP_AUTH0_API_AUDIENCE,
    auth0PortalDomain: response.data.REACT_APP_AUTH0_PORTAL_DOMAIN,
    auth0PortalClientId: response.data.REACT_APP_AUTH0_PORTAL_CLIENT_ID,
    auth0PortalApiAudience: response.data.REACT_APP_AUTH0_PORTAL_API_AUDIENCE,
    splitioApiKey: response.data.REACT_APP_SPLITIO_BROWSER_API_KEY,
    tranzilaNotifyURLAddress:
      response.data.REACT_APP_TRANZILA_NOTIFY_URL_ADDRESS,
  };
}
